import { Typography } from '@material-ui/core';
import { useEffect, useRef } from 'react';
import type { CockpitArtifactContentState } from '../controller/cockpitControllerTypes';
import type { CockpitArtifactView } from '../model/cockpitViewModel';
import { ArtifactContentPanel } from './ArtifactContentPanel';
import {
  ArtifactLimitations,
  ArtifactRecordStrip,
  artifactDateLabel,
  artifactIdentityLine,
} from './ArtifactRecord';
import { useCockpitStyles } from './cockpitStyles';

/** Content loaders owned by the controller and threaded down to the open card. */
export interface ArtifactContentAccess {
  artifactContent?: Record<string, CockpitArtifactContentState>;
  loadArtifactContent?: (artifactId: string) => Promise<string>;
  loadMoreArtifactContent?: (artifactId: string) => Promise<string>;
}

export type ArtifactJourneyItemProps = ArtifactContentAccess & {
  artifact: CockpitArtifactView;
  index: number;
  total: number;
  open: boolean;
  onSelect: (id: string) => void;
};

const ArtifactHeaderLines = ({
  artifact,
  position,
  open,
}: {
  artifact: CockpitArtifactView;
  position: string;
  open: boolean;
}) => {
  const classes = useCockpitStyles();
  return (
    <span className={classes.artifactSequenceLines}>
      {open ? (
        <>
          <Typography component="span" variant="h6">
            {artifact.label}
          </Typography>
          <Typography component="span" variant="caption">
            {artifactIdentityLine(artifact, position)}
          </Typography>
        </>
      ) : (
        <>
          <Typography
            component="span"
            className={classes.artifactSequencePosition}
          >
            {position}
          </Typography>
          <Typography
            component="span"
            variant="body1"
            className={classes.artifactSequenceName}
          >
            {artifact.label}
          </Typography>
        </>
      )}
      <Typography component="span" variant="body2">
        {artifactDateLabel(artifact.createdAt)}
      </Typography>
    </span>
  );
};

const ArtifactOpenContent = ({
  artifact,
  artifactContent,
  loadArtifactContent,
  loadMoreArtifactContent,
}: { artifact: CockpitArtifactView } & ArtifactContentAccess) => {
  const classes = useCockpitStyles();
  return (
    <>
      <ArtifactRecordStrip artifact={artifact} />
      <div className={classes.artifactOpenBody}>
        <div className={classes.artifactReadingColumn}>
          <ArtifactContentPanel
            artifact={artifact}
            contentState={artifactContent?.[artifact.id]}
            loadContent={loadArtifactContent}
            loadMore={loadMoreArtifactContent}
          />
          <ArtifactLimitations artifact={artifact} />
        </div>
      </div>
    </>
  );
};

const ArtifactRevealHeader = ({
  artifact,
  position,
  open,
  onSelect,
  headerRef,
}: {
  artifact: CockpitArtifactView;
  position: string;
  open: boolean;
  onSelect: (id: string) => void;
  headerRef: React.RefObject<HTMLButtonElement>;
}) => {
  const classes = useCockpitStyles();
  return (
    <h3 className={classes.artifactSequenceHeading}>
      <button
        type="button"
        ref={headerRef}
        className={`${classes.artifactSequenceButton} ${
          open ? classes.artifactSequenceButtonOpen : ''
        }`}
        aria-label={`${open ? 'Fechar' : 'Abrir'} ${
          artifact.label
        }, ${position.toLocaleLowerCase('pt-BR')}`}
        aria-expanded={open}
        onClick={() => onSelect(artifact.id)}
      >
        <ArtifactHeaderLines
          artifact={artifact}
          position={position}
          open={open}
        />
        <Typography
          component="span"
          variant="button"
          className={classes.artifactSequenceToggle}
        >
          {open ? 'Fechar' : 'Abrir'}
        </Typography>
      </button>
    </h3>
  );
};

export const ArtifactJourneyItem = ({
  artifact,
  index,
  total,
  open,
  onSelect,
  ...content
}: ArtifactJourneyItemProps) => {
  const classes = useCockpitStyles();
  const headerRef = useRef<HTMLButtonElement>(null);
  useHeaderFocusOnOpen(open, headerRef);
  const position = `Artefato ${index + 1} de ${total}`;
  return (
    <li
      className={`${classes.artifactSequenceItem} ${
        open ? classes.artifactSequenceSelected : ''
      }`}
    >
      <span className={classes.artifactSequenceMarker} aria-hidden="true" />
      <ArtifactRevealHeader
        artifact={artifact}
        position={position}
        open={open}
        onSelect={onSelect}
        headerRef={headerRef}
      />
      {open && <ArtifactOpenContent artifact={artifact} {...content} />}
    </li>
  );
};

/**
 * Selecting the artifact node in the evidence flow opens this card, so the
 * header takes focus unless the reader is already inside it.
 */
function useHeaderFocusOnOpen(
  open: boolean,
  headerRef: React.RefObject<HTMLButtonElement>,
): void {
  useEffect(() => {
    if (!open) return;
    const header = headerRef.current;
    if (!header || header.contains(document.activeElement)) return;
    header.focus();
  }, [open, headerRef]);
}
